/**
 * Reynisfjara's STUÐLABERG — the basalt columns — in the shared 120×120 LINE
 * asset box.
 *
 * The Home tab, all four passcode dots and the chat wallpaper draw this cluster.
 *
 * WHY THIS REPLACED A PUFFIN
 *
 * The puffin was itself a replacement: the theme's first glyph was a set of sea
 * stacks, dropped as the wrong subject. The bird then failed for a different and
 * more basic reason — the owner read it as a PENGUIN (2026-08-16). That is a
 * verdict on legibility rather than on taste, and it is not something a redraw
 * fixes: a small upright black-and-white seabird IS a penguin to almost everyone,
 * and the two marks that separate a puffin from one (the bill plates and the eye
 * patch) are the two that disappear first at 22px.
 *
 * The columns are the honest answer. Reynisfjara is famous for exactly this wall
 * — the hexagonal organ pipes people photograph and climb — and it is the one
 * subject here that has no rival reading. Nothing else in a chat theme is a row
 * of packed verticals with broken flat tops.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - THE COLUMNS TOUCH. Basalt columns are packed against each other with no gap;
 *    spaced out they become a fence, a graph or a set of buildings. The only
 *    separation is a hairline of shadow, which is why the faces alternate in tone
 *    rather than being drawn apart.
 *  - THE TOPS ARE FLAT AND STEPPED, never pointed and never level. Pointed reads
 *    as a crystal, level reads as a wall, and the stepped silhouette is the whole
 *    identification.
 *  - THEY ARE VERTICAL. A leaning column is a fallen one — this glyph has no
 *    per-digit tilt for the same reason Prague's lamp barely has one.
 *  - THE HEIGHTS ARE IRREGULAR AND CLUSTERED, tallest off-centre. Evenly stepped
 *    heights read as a bar chart, which is the one modern object this silhouette
 *    can be mistaken for.
 */

const f = ( v: number ) => +v.toFixed( 1 );

/* ---------------------------------------------------------------- geometry -- */

/** Where the cluster stands and how wide it runs. */
const BASE = 106, LEFT = 22;
/**
 * SIX columns. Five reads as a hand, seven starts to close into a solid block at
 * tab size; six is the count that stays a cluster.
 *
 * Each entry is [ width, top ]. The tallest is the second from the left rather
 * than the middle — a symmetrical cluster reads as a crown.
 */
const COLUMNS: [ number, number ][] = [
  [ 12, 54 ],
  [ 14, 26 ],
  [ 11, 36 ],
  [ 13, 20 ],
  [ 12, 44 ],
  [ 11, 62 ],
];

/** How deep the broken top face is on each column. */
const CAP = 4.5;

const xs = ( () => {
  const out: number[] = [];
  let x = LEFT;
  for ( const [ w ] of COLUMNS ) { out.push( x ); x += w; }
  out.push( x );
  return out;
} )();

/**
 * The column SHAFTS, below their broken tops.
 *
 * Every other one is exported separately so a caller can tint the pair — see
 * BASALT_FACES_ALT. Drawn as one path each way rather than six paths, because at
 * 22px six fills is six chances for the rasteriser to leave a seam between two
 * columns that are supposed to be touching.
 */
function shafts( pick: ( i: number ) => boolean ): string {
  const out: string[] = [];
  for ( let i = 0; i < COLUMNS.length; i++ ) {
    if ( !pick( i ) ) { continue; }
    const top = COLUMNS[ i ][ 1 ] + CAP;
    out.push( `M${ f( xs[ i ] ) },${ BASE } L${ f( xs[ i ] ) },${ f( top ) }`
      + ` L${ f( xs[ i + 1 ] ) },${ f( top ) } L${ f( xs[ i + 1 ] ) },${ BASE } Z` );
  }
  return out.join( ' ' );
}

/** The lit faces — columns 0, 2, 4. */
export const BASALT_FACES = shafts( ( i ) => i % 2 === 0 );
/** The shaded faces — columns 1, 3, 5. Alternating tone is what separates
 *  columns that physically touch, without drawing a gap between them. */
export const BASALT_FACES_ALT = shafts( ( i ) => i % 2 === 1 );

/**
 * The broken TOPS, one flat cap per column.
 *
 * They are what makes the silhouette stepped rather than serrated, and in the lit
 * state they take the palest tone in the set — a fresh basalt break is much
 * lighter than the weathered face under it, which is the one piece of colour
 * information this rock has.
 */
export const BASALT_TOPS = ( () => {
  const out: string[] = [];
  for ( let i = 0; i < COLUMNS.length; i++ ) {
    const t = COLUMNS[ i ][ 1 ];
    out.push( `M${ f( xs[ i ] ) },${ f( t + CAP ) } L${ f( xs[ i ] ) },${ f( t ) }`
      + ` L${ f( xs[ i + 1 ] ) },${ f( t ) } L${ f( xs[ i + 1 ] ) },${ f( t + CAP ) } Z` );
  }
  return out.join( ' ' );
} )();

/** The whole cluster as ONE path — shafts and caps together. This is what the
 *  idle tab and the unentered passcode dot draw: an unlit cluster is one
 *  silhouette and its columns are simply part of it. */
export const BASALT = `${ BASALT_FACES } ${ BASALT_FACES_ALT } ${ BASALT_TOPS }`;

/**
 * A SIMPLER cluster for the wallpaper's smallest grains: four columns instead of
 * six, and no caps.
 *
 * Same reason every module here has one. At a third of this box the caps are a
 * unit and a half and fill in, and six columns at that width land at three
 * quarters of a pixel each — the cluster turns into a smudge with a ragged top.
 * Four wider columns keep the rhythm that identifies it.
 */
export const BASALT_SIMPLE = ( () => {
  const out: string[] = [];
  const wide: [ number, number ][] = [ [ 18, 50 ], [ 20, 24 ], [ 19, 34 ], [ 17, 58 ] ];
  let x = 22;
  for ( const [ w, top ] of wide ) {
    out.push( `M${ f( x ) },${ BASE } L${ f( x ) },${ f( top ) }`
      + ` L${ f( x + w ) },${ f( top ) } L${ f( x + w ) },${ BASE } Z` );
    x += w;
  }
  return out.join( ' ' );
} )();

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered clusters, on the DARK upper zone this skin's passcode screen
 * paints (#2A3138).
 *
 * They LIGHT UP the way the beach does at dusk — wet slate, then the first grey
 * of the sky catching the tops, then dry basalt, then the pale driftwood the
 * theme keeps for its warmest mark. It is the same ramp the puffins walked and it
 * measured well; the drawing changed under it, not the colour thinking.
 *
 * Measured on that zone: 2.29 / 3.48 / 4.18 / 5.92 to 1, monotonic.
 */
export const BASALT_PC_COLORS = [ '#5D6773', '#7D848D', '#9A907F', '#C5A98F' ];

/**
 * ONE tone for every unentered cluster — the frame never changes per digit (the
 * rule Aurora's orbs settled).
 *
 * 1.06:1 on the dark zone: barely off the ground it stands on, which is what a
 * lock screen wants and what only a dark skin can give. The lit ones run from
 * 2.29 to 5.92, so an entered digit outweighs an empty one by two to five times.
 */
export const BASALT_PC_OFF = '#2E353E';

/**
 * THE HEXAGON — one basalt column seen end-on, and the chat wallpaper's grain.
 *
 * The wallpaper drew the column CLUSTER for one pass and the owner asked for
 * something simpler (2026-08-16). This is the simplest mark the subject has and
 * it is still unmistakably the subject: what makes stuðlaberg famous is that the
 * columns are hexagonal, and a scatter of hexagons on black sand is the top of
 * that pavement.
 *
 * It also solves the problem the cluster has at wallpaper size. A cluster needs
 * six columns, their height steps and their caps to read; a hexagon needs six
 * straight lines, and it survives at any size a chat wallpaper renders. This is
 * the same move Ocean's simple medusa and Prague's simple lamp make, except that
 * here the simplification lands on a DIFFERENT shape rather than a thinner
 * version of the same one.
 *
 * FLAT-TOPPED, not pointy-topped. A hexagon standing on a vertex reads as a gem
 * or a warning sign; resting on a flat edge it reads as a paving stone, which is
 * what a column top in a pavement is.
 */
export const BASALT_HEX = ( () => {
  const R = 34, CX = 60, CY = 60;
  const pts: string[] = [];
  for ( let i = 0; i < 6; i++ ) {
    const a = ( i * 60 + 30 ) * Math.PI / 180;
    pts.push( `${ f( CX + Math.cos( a ) * R ) },${ f( CY + Math.sin( a ) * R ) }` );
  }
  return 'M' + pts.join( ' L' ) + ' Z';
} )();
